/**
 * Recommendation Card Component
 *
 * Full recommendation card for the recommendations list screen.
 * Displays comprehensive recommendation data including:
 * - EXIT/WATCH type indicator badge (red for EXIT, amber for WATCH)
 * - Fund name prominently displayed
 * - Reason text explaining why action is recommended
 * - Current value display
 * - Switch suggestion preview (replacement fund name if applicable)
 * - Tax impact summary (STCG/LTCG amounts)
 * - Clickable card that navigates to recommendation detail
 */

import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatINR, formatINRCompact } from "@/lib/utils";
import {
  LogOut,
  Eye,
  RefreshCw,
  TrendingUp,
  Pause,
  ChevronRight,
  ArrowRightLeft,
  Receipt,
} from "lucide-react";
import type { FundRating } from "@curie/shared";

// =============================================================================
// Types
// =============================================================================

/**
 * Recommendation types supported by the card
 */
export type RecommendationCardType =
  | "exit"
  | "watch"
  | "switch"
  | "increase-sip"
  | "start-sip"
  | "hold";

/**
 * Priority levels for recommendations
 */
export type RecommendationCardPriority = "high" | "medium" | "low";

/**
 * Tax impact summary for the recommendation
 */
export interface RecommendationTaxImpact {
  /** Short-term capital gains amount */
  stcg: number;
  /** Long-term capital gains amount */
  ltcg: number;
  /** Total estimated tax */
  totalTax: number;
}

/**
 * Fund summary for display
 */
export interface RecommendationFundSummary {
  /** Fund name */
  name: string;
  /** Fund rating */
  rating: FundRating;
  /** Fund rank in category */
  rank: number;
  /** Total funds in category */
  totalFundsInCategory: number;
}

/**
 * Data required for the recommendation card
 */
export interface RecommendationCardData {
  /** Unique identifier for the recommendation */
  id: string;
  /** Type of recommendation (exit, watch, switch, etc.) */
  type: RecommendationCardType;
  /** Priority level */
  priority: RecommendationCardPriority;
  /** Fund being recommended to exit/switch from */
  fromFund?: RecommendationFundSummary;
  /** Recommended replacement fund (for switch recommendations) */
  toFund?: RecommendationFundSummary;
  /** Reason/explanation for the recommendation */
  reason: string;
  /** Current value of the holding (if applicable) */
  currentValue?: number;
  /** Tax impact summary */
  taxImpact?: RecommendationTaxImpact;
}

/**
 * Props for the RecommendationCard component
 */
export interface RecommendationCardProps {
  /** Recommendation data */
  data: RecommendationCardData;
  /** Click handler for navigation */
  onClick?: (id: string) => void;
  /** Additional CSS classes */
  className?: string;
}

// =============================================================================
// Constants
// =============================================================================

/**
 * Configuration for recommendation types
 * Defines visual appearance, icons, and labels
 */
const TYPE_CONFIG: Record<
  RecommendationCardType,
  {
    label: string;
    icon: React.ElementType;
    badgeClassName: string;
    iconClassName: string;
    borderAccent: string;
  }
> = {
  exit: {
    label: "EXIT",
    icon: LogOut,
    badgeClassName:
      "bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-950 dark:text-rose-300 dark:border-rose-800",
    iconClassName: "text-rose-600 dark:text-rose-400",
    borderAccent: "border-l-rose-500",
  },
  watch: {
    label: "WATCH",
    icon: Eye,
    badgeClassName:
      "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800",
    iconClassName: "text-amber-600 dark:text-amber-400",
    borderAccent: "border-l-amber-500",
  },
  switch: {
    label: "SWITCH",
    icon: RefreshCw,
    badgeClassName:
      "bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-950 dark:text-sky-300 dark:border-sky-800",
    iconClassName: "text-sky-600 dark:text-sky-400",
    borderAccent: "border-l-sky-500",
  },
  "increase-sip": {
    label: "INCREASE SIP",
    icon: TrendingUp,
    badgeClassName:
      "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800",
    iconClassName: "text-emerald-600 dark:text-emerald-400",
    borderAccent: "border-l-emerald-500",
  },
  "start-sip": {
    label: "START SIP",
    icon: TrendingUp,
    badgeClassName:
      "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800",
    iconClassName: "text-emerald-600 dark:text-emerald-400",
    borderAccent: "border-l-emerald-500",
  },
  hold: {
    label: "HOLD",
    icon: Pause,
    badgeClassName:
      "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
    iconClassName: "text-slate-600 dark:text-slate-400",
    borderAccent: "border-l-slate-500",
  },
};

/**
 * Rating badge configuration
 */
const RATING_CONFIG: Record<
  FundRating,
  {
    label: string;
    className: string;
  }
> = {
  "in-form": {
    label: "In-Form",
    className:
      "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800",
  },
  "on-track": {
    label: "On-Track",
    className:
      "bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-900/30 dark:text-sky-400 dark:border-sky-800",
  },
  "off-track": {
    label: "Off-Track",
    className:
      "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800",
  },
  "out-of-form": {
    label: "Out-of-Form",
    className:
      "bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800",
  },
};

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Type badge with icon
 */
function TypeBadge({ type }: { type: RecommendationCardType }) {
  const config = TYPE_CONFIG[type];
  const Icon = config.icon;

  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1 font-semibold uppercase tracking-wide text-xs",
        config.badgeClassName
      )}
    >
      <Icon className={cn("h-3 w-3", config.iconClassName)} />
      {config.label}
    </Badge>
  );
}

/**
 * Rating badge for fund
 */
function RatingBadge({ rating }: { rating: FundRating }) {
  const config = RATING_CONFIG[rating];

  return (
    <Badge
      variant="outline"
      className={cn("font-medium text-xs border", config.className)}
    >
      {config.label}
    </Badge>
  );
}

/**
 * Switch suggestion preview showing from -> to fund
 */
function SwitchPreview({
  fromFund,
  toFund,
}: {
  fromFund: RecommendationFundSummary;
  toFund: RecommendationFundSummary;
}) {
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 text-sm">
      <div className="flex-1 min-w-0">
        <p className="text-muted-foreground text-xs">From</p>
        <p className="font-medium truncate">{fromFund.name}</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <RatingBadge rating={fromFund.rating} />
          <span className="text-xs text-muted-foreground">
            Rank {fromFund.rank}/{fromFund.totalFundsInCategory}
          </span>
        </div>
      </div>

      <ArrowRightLeft className="h-4 w-4 text-muted-foreground shrink-0" />

      <div className="flex-1 min-w-0">
        <p className="text-muted-foreground text-xs">To</p>
        <p className="font-medium truncate">{toFund.name}</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <RatingBadge rating={toFund.rating} />
          <span className="text-xs text-muted-foreground">
            Rank {toFund.rank}/{toFund.totalFundsInCategory}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Tax impact summary row
 */
function TaxImpactSummary({ taxImpact }: { taxImpact: RecommendationTaxImpact }) {
  const hasStcg = taxImpact.stcg > 0;
  const hasLtcg = taxImpact.ltcg > 0;

  if (!hasStcg && !hasLtcg) {
    return null;
  }

  return (
    <div className="flex items-center gap-3 p-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
      <Receipt className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
      <div className="flex-1 flex items-center gap-3 text-sm">
        {hasStcg && (
          <div>
            <span className="text-muted-foreground">STCG:</span>{" "}
            <span className="font-medium text-amber-700 dark:text-amber-400">
              {formatINRCompact(taxImpact.stcg)}
            </span>
          </div>
        )}
        {hasLtcg && (
          <div>
            <span className="text-muted-foreground">LTCG:</span>{" "}
            <span className="font-medium text-amber-700 dark:text-amber-400">
              {formatINRCompact(taxImpact.ltcg)}
            </span>
          </div>
        )}
        <div className="ml-auto">
          <span className="text-muted-foreground">Tax:</span>{" "}
          <span className="font-semibold text-amber-700 dark:text-amber-400">
            {formatINRCompact(taxImpact.totalTax)}
          </span>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Recommendation Card
 *
 * Full-featured recommendation card for displaying comprehensive recommendation
 * data in list views. Supports navigation to detail view.
 *
 * @example
 * ```tsx
 * <RecommendationCard
 *   data={{
 *     id: "rec-1",
 *     type: "switch",
 *     priority: "high",
 *     fromFund: {
 *       name: "Nippon India Growth Fund",
 *       rating: "off-track",
 *       rank: 15,
 *       totalFundsInCategory: 28,
 *     },
 *     toFund: {
 *       name: "Kotak Emerging Equity Fund",
 *       rating: "in-form",
 *       rank: 2,
 *       totalFundsInCategory: 28,
 *     },
 *     reason: "Fund has been underperforming for 18 months...",
 *     currentValue: 40076,
 *     taxImpact: { stcg: 0, ltcg: 8376, totalTax: 1047 },
 *   }}
 *   onClick={(id) => navigate(`/recommendations/${id}`)}
 * />
 * ```
 */
export function RecommendationCard({
  data,
  onClick,
  className,
}: RecommendationCardProps) {
  const {
    id,
    type,
    fromFund,
    toFund,
    reason,
    currentValue,
    taxImpact,
  } = data;

  const config = TYPE_CONFIG[type];
  const fundName = fromFund?.name ?? toFund?.name ?? "Unknown Fund";

  const handleClick = () => {
    onClick?.(id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  const cardContent = (
    <Card
      className={cn(
        "w-full overflow-hidden transition-all duration-200 border-l-4",
        config.borderAccent,
        onClick && "cursor-pointer hover:shadow-md hover:border-primary/20",
        className
      )}
      onClick={onClick ? handleClick : undefined}
      onKeyDown={onClick ? handleKeyDown : undefined}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? "button" : undefined}
      aria-label={onClick ? `View recommendation for ${fundName}` : undefined}
    >
      <CardContent className="p-4 space-y-3">
        {/* Header Row: Type Badge + Fund Name + Value */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0 space-y-1.5">
            <TypeBadge type={type} />
            <h3 className="font-semibold text-base leading-tight line-clamp-2">
              {fundName}
            </h3>
            {fromFund && !toFund && (
              <div className="flex items-center gap-1.5">
                <RatingBadge rating={fromFund.rating} />
                <span className="text-xs text-muted-foreground">
                  Rank {fromFund.rank}/{fromFund.totalFundsInCategory}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {currentValue !== undefined && (
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Current Value</p>
                <p className="font-semibold">{formatINR(currentValue)}</p>
              </div>
            )}
            {onClick && (
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
        </div>

        {/* Reason Text */}
        <p className="text-sm text-muted-foreground line-clamp-2">{reason}</p>

        {/* Switch Preview (for switch recommendations) */}
        {type === "switch" && fromFund && toFund && (
          <SwitchPreview fromFund={fromFund} toFund={toFund} />
        )}

        {/* Tax Impact Summary */}
        {taxImpact && <TaxImpactSummary taxImpact={taxImpact} />}
      </CardContent>
    </Card>
  );

  // Wrap in Link if onClick is provided
  if (onClick) {
    return (
      <Link
        to="/recommendations/$recommendationId"
        params={{ recommendationId: id }}
        className="block"
      >
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}

// =============================================================================
// Skeleton Loader
// =============================================================================

/**
 * Skeleton loader for RecommendationCard
 */
export function RecommendationCardSkeleton({
  className,
}: {
  className?: string;
}) {
  return (
    <Card
      className={cn(
        "w-full overflow-hidden border-l-4 border-l-muted",
        className
      )}
    >
      <CardContent className="p-4 space-y-3">
        {/* Header Row Skeleton */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0 space-y-1.5">
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-5 w-3/4" />
            <div className="flex items-center gap-1.5">
              <Skeleton className="h-4 w-14 rounded-full" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="text-right space-y-1">
              <Skeleton className="h-3 w-16 ml-auto" />
              <Skeleton className="h-5 w-20" />
            </div>
            <Skeleton className="h-5 w-5 rounded" />
          </div>
        </div>

        {/* Reason Skeleton */}
        <div className="space-y-1">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-4/5" />
        </div>

        {/* Switch Preview Skeleton */}
        <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
          <div className="flex-1 min-w-0 space-y-1">
            <Skeleton className="h-3 w-8" />
            <Skeleton className="h-4 w-3/4" />
            <div className="flex items-center gap-1.5">
              <Skeleton className="h-4 w-12 rounded-full" />
              <Skeleton className="h-3 w-12" />
            </div>
          </div>
          <Skeleton className="h-4 w-4 rounded shrink-0" />
          <div className="flex-1 min-w-0 space-y-1">
            <Skeleton className="h-3 w-8" />
            <Skeleton className="h-4 w-3/4" />
            <div className="flex items-center gap-1.5">
              <Skeleton className="h-4 w-12 rounded-full" />
              <Skeleton className="h-3 w-12" />
            </div>
          </div>
        </div>

        {/* Tax Impact Skeleton */}
        <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/30">
          <Skeleton className="h-4 w-4 rounded shrink-0" />
          <div className="flex-1 flex items-center gap-3">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-20 ml-auto" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// List Component
// =============================================================================

/**
 * Props for the recommendations list
 */
export interface RecommendationCardListProps {
  /** Array of recommendations to display */
  recommendations: RecommendationCardData[];
  /** Click handler for recommendation items */
  onRecommendationClick?: (id: string) => void;
  /** Additional CSS classes */
  className?: string;
  /** Empty state message */
  emptyMessage?: string;
}

/**
 * Recommendation Card List
 *
 * Renders a list of recommendation cards.
 *
 * @example
 * ```tsx
 * <RecommendationCardList
 *   recommendations={recommendations}
 *   onRecommendationClick={(id) => navigate(`/recommendations/${id}`)}
 *   emptyMessage="No recommendations at this time"
 * />
 * ```
 */
export function RecommendationCardList({
  recommendations,
  onRecommendationClick,
  className,
  emptyMessage = "No recommendations",
}: RecommendationCardListProps) {
  if (recommendations.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      {recommendations.map((recommendation) => (
        <RecommendationCard
          key={recommendation.id}
          data={recommendation}
          onClick={onRecommendationClick}
        />
      ))}
    </div>
  );
}

/**
 * Skeleton loader for the recommendation card list
 */
export function RecommendationCardListSkeleton({
  count = 3,
  className,
}: {
  count?: number;
  className?: string;
}) {
  return (
    <div className={cn("space-y-3", className)}>
      {[...Array(count)].map((_, i) => (
        <RecommendationCardSkeleton key={i} />
      ))}
    </div>
  );
}

export default RecommendationCard;
